import React from "react"
import ReactModal from "react-modal"
import { inject, observer } from "mobx-react"
import styled, { ThemeProvider } from "styled-components"
import { Grid } from "react-styled-flexboxgrid"

import FixedCloseButton from "~/components/modals/FixedCloseButton"
import MODAL_STYLES from "~/components/modals/styles"
import Spacer from "~/components/Spacer"
import SectionHeader from "~/components/Pages/SectionHeader"
import Center from "~/components/basic/Center"
import ContinueWithButtons from "~/components/auth/ContinueWithButtons"
import ErrorText from "~/components/Form/ErrorText"
import PhoneNumber from "~/components/traffic-tickets/steps/UserInfo/PhoneNumber"
import Email from "~/components/traffic-tickets/steps/UserInfo/Email"
import FullName from "~/components/traffic-tickets/steps/UserInfo/FullName"
import * as StringTools from "~/utilities/string"
import WinItButton from "~/components/WinItButton"
import Car from "~/public/static/images/png/car.png"
import Checkbox from "~/components/Form/Checkbox"

const FlexBoxGridTheme = {
  flexboxgrid: {
    gridSize: 2,
    gutterWidth: "1",
    outerMargin: "0",
    mediaQuery: "only screen",
    container: {
      sm: 38,
      md: 46,
      lg: 76,
    },
    breakpoints: {
      xs: 0,
      sm: 38,
      md: 50,
      lg: 75,
    },
  },
}

const BackgroundLayerOne = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex-grow: 1;
  background-image: url(${Car});
  background-repeat: no-repeat;
  background-position: 100% 100%;
  background-size: 377px 136px;
  padding: ${(props) => (props.isMobile ? 1 : 2)}rem;

  overflow-y: auto;
  max-height: 90vh;
`

const SignUpContainer = styled.form`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
`

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 15px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const InputWrapper = styled.div`
  width: 100%;
  flex: 1;

  & > * {
    width: 100%;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const ButtonWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    flex: 1;
    width: 100%;
    max-width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

// const Text = styled.div`
//   font-size: 0.8em;
//   color: ${(props) => props.theme.colors.text.label};
//   margin: 1em 0 1em 0;
//   text-align: center;
//   a {
//     color: ${(props) => props.theme.colors.text.label};
//     text-decoration: underline;
//   }
// `

const NavLink = styled.a`
  display: inline-flex;
  font-size: 0.85em;
  color: #8a8a8a;
  margin: 0 0.7em;
  ${(props) => props.theme.media.mobile`
    margin: 0.6em 0;
  `};
  cursor: pointer;
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  text-decoration: none;
  text-align: center;
  &:hover {
    text-decoration: underline;
  }
`

// Show error when collapsed
const ErrorContainer = styled.div`
  display: "flex";
  ${(props) => props.theme.media.smallDesktop`
    display: 'none'
  `}
  font-size: 0.9em;
`
const AgreementContainer = styled.div`
  max-width: 100%;
  max-height: 150px;
  overflow-y: auto;
  background: #f9f9f9;
  padding: 10px;
  border-radius: 5px;
  font-size: 1em;
  line-height: 1.4;
  color: ${(props) => props.theme.colors.text.label};

  a {
    color: ${(props) => props.theme.colors.text.label};
    text-decoration: underline;
    font-weight: 600;
  }
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-top: 12px;
  padding-left: 10px;
`

const StyledCheckbox = styled(Checkbox)`
  flex-shrink: 0;
  margin-top: 3px;
`

const Text = styled.div`
  font-size: 0.75em;
  color: ${(props) => props.theme.colors.text.label};
  line-height: 1.5;
  text-align: justify;
  width: 100%;
  max-width: 100%;

  a {
    color: ${(props) => props.theme.colors.text.label};
    text-decoration: underline;
    font-weight: 600;
  }
`

const ErrorWrapper = styled.div`
  text-align: center;
  min-height: 25px;
`

@inject((all) => ({
  isMobile: all.store.ui.isMobile,
  auth: all.store.auth,
  isLoggedIn: all.store.auth.isLoggedIn,
  user: all.store.auth.user,
  loginModalShowing: all.store.ui.loginModalShowing,
  setLoginModalShowing: all.store.ui.setLoginModalShowing,
  signupModalShowing: all.store.ui.signupModalShowing,
  setSignupModalShowing: all.store.ui.setSignupModalShowing,
  setPhoneVerifyModalShowing: all.store.ui.setPhoneVerifyModalShowing,
  isStateAffiliatedWithWinitLaw: all.store.trafficSubmission.isStateAffiliatedWithWinitLaw,
}))
@observer
class SignUpModal extends React.Component {
  constructor() {
    super()
    this.state = {
      isWinitTermsAccepted: false,
      isFormSubmitted: false,
    }
  }

  openModal = () => this.props.setSignupModalShowing(true)

  hideModal = () => this.props.setSignupModalShowing(false)

  swapModal = () => {
    this.hideModal()
    this.props.setLoginModalShowing(true)
  }

  toggleWinitTOS = () => {
    this.setState({
      isWinitTermsAccepted: !this.state.isWinitTermsAccepted,
    })
  }

  handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault()
    const { auth } = this.props
    this.setState({
      isFormSubmitted: true,
    })
    if (!this.state.isWinitTermsAccepted) return
    const temporaryPassword = StringTools.random(15)
    auth.user.changeKey("password", temporaryPassword)
    await auth.signup()
  }

  render() {
    const { isMobile, auth, isLoggedIn, isStateAffiliatedWithWinitLaw } = this.props
    const asyncError = auth.validateDisplayMessage("asyncError")
    const signing =
      this.props.auth.isAttemptingSignUp || this.props.auth.isAttemptingLogIn

    return (
      <>
        <ReactModal
          closeTimeoutMS={300}
          isOpen={this.props.signupModalShowing && !isLoggedIn}
          style={
            isMobile
              ? MODAL_STYLES.mobile.generalNoPadding
              : MODAL_STYLES.desktop.generalNoPadding
          }
          contentLabel="Create a WinIt account"
          onRequestClose={this.hideModal} // for escape key or outside click
        >
          <FixedCloseButton
            color={this.props.isMobile ? "black" : "white"}
            onClick={this.hideModal}
          />
          <ThemeProvider theme={FlexBoxGridTheme}>
            <BackgroundLayerOne isMobile={isMobile}>
              <SectionHeader title="Sign Up" />
              <Spacer mobile={10} desktop={20} />
              <Center>
                <ErrorText
                  margin="5px 0 10px 0"
                  fontSize="1rem"
                  error={asyncError}
                />
                Have an account already?{" "}
                <NavLink onClick={this.swapModal}>Log In</NavLink>
              </Center>

              <Grid style={{ width: "100%" }}>
                <SignUpContainer
                  isMobile={isMobile}
                  onSubmit={this.handleSubmit}
                >
                  <RowContainer>
                    <InputWrapper>
                      <FullName disabled={signing} />
                    </InputWrapper>
                    <InputWrapper>
                      <PhoneNumber disabled={signing} />
                    </InputWrapper>
                  </RowContainer>
                  <RowContainer>
                    <InputWrapper>
                      <Email disabled={signing} />
                    </InputWrapper>
                  </RowContainer>
                  <Spacer mobile={10} desktop={10} />
                  <ButtonContainer>
                    <ButtonWrapper>
                      <WinItButton
                        onClick={this.handleSubmit}
                        type="submit"
                        width="100%"
                      >
                        {signing ? "Signing Up..." : "Sign Up"}
                      </WinItButton>
                    </ButtonWrapper>
                    <ButtonWrapper>
                      <ContinueWithButtons />
                    </ButtonWrapper>
                  </ButtonContainer>
                </SignUpContainer>
              </Grid>
              <ErrorContainer>
                <CheckboxContainer>
                  <StyledCheckbox
                    checked={this.state.isWinitTermsAccepted}
                    onClick={this.toggleWinitTOS}
                    size={18}
                  />
                  <Text>
                    I agree to the{" "}
                    <a
                      href="/terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Winit Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </Text>
                </CheckboxContainer>

                <AgreementContainer>
                  <Text>
                    You also agree to receive promotional and personalized
                    marketing, informational, and administrative calls, texts,
                    and email messages (e.g., cart reminders) from WinIt about
                    your current and future traffic tickets at the phone number
                    and email provided above until you opt-out, which you may do
                    at any time by emailing{" "}
                    <a
                      href="mailto:support@appwinit.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      support@appwinit.com
                    </a>{" "}
                    with the word “optout” in the subject line, or by following
                    the opt-out instructions in any message that you receive.
                    <br />
                    <br />
                    You consent even if your phone number is listed on a Do Not
                    Call Registry. Reply STOP to unsubscribe or HELP for
                    assistance. Message and data rates may apply. Message
                    frequency may vary depending on how many traffic tickets you
                    receive.
                    <br />
                    <br />
                    You represent that the phone number and e-mail address you
                    provide to us belong to you and no one else and agree to
                    promptly notify us of any changes by sending an email to{" "}
                    <a
                      href="mailto:support@appwinit.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      support@appwinit.com
                    </a>
                    .
                    <br />
                    <br />
                    You affirm that you have read and agree to our{" "}
                    {isStateAffiliatedWithWinitLaw ? (
                      <a
                        href="https://winitlaw.com/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>
                    ) : (
                      <a
                        href="/terms-of-use"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>
                    )}
                    {" "}
                    and{" "}
                    {isStateAffiliatedWithWinitLaw ? (
                      <a
                        href="https://winitlaw.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    ) : (
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    )}
                    {" "}
                    to learn how we treat your personal information.
                  </Text>
                </AgreementContainer>

                <ErrorWrapper>
                  <ErrorText
                    error={
                      !this.state.isWinitTermsAccepted &&
                      this.state.isFormSubmitted &&
                      "Please tick this box to confirm that you agree"
                    }
                  />
                </ErrorWrapper>
              </ErrorContainer>
            </BackgroundLayerOne>
          </ThemeProvider>
        </ReactModal>
        {!this.props.auth.isLoggedIn ? (
          <this.props.opener onClick={this.openModal} />
        ) : null}
      </>
    )
  }
}

export default SignUpModal
